/**
 * Authentication Service
 * ======================
 * Handles all authentication operations using Supabase Auth
 */

import { supabase, getUserRole, getUserProfile } from './supabaseClient';

/**
 * Sign up a new user (automatically creates as customer)
 */
export const signUp = async (email, password, additionalData = {}) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: 'customer',
          ...additionalData
        }
      }
    });

    if (error) throw error;

    return {
      success: true,
      user: data.user,
      session: data.session,
      message: 'Account created successfully! Please check your email to verify your account.'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign in with email and password
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;

    // Get user role
    const role = await getUserRole(data.user.id);
    const profile = await getUserProfile(data.user.id, role);

    return {
      success: true,
      user: data.user,
      session: data.session,
      role,
      profile
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign in with OAuth provider (Google, Facebook, Twitter, GitHub)
 */
export const signInWithProvider = async (provider) => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) throw error;

    return {
      success: true,
      data
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign out
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    return {
      success: true,
      message: 'Signed out successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get current authenticated user with role and profile
 */
export const getCurrentUserWithRole = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) throw error;
    if (!user) return null;

    const role = await getUserRole(user.id);
    const profile = await getUserProfile(user.id, role);

    return {
      user,
      role,
      profile
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Reset password (send reset email)
 */
export const resetPassword = async (email) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) throw error;

    return {
      success: true,
      message: 'Password reset email sent! Check your inbox.'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update password
 */
export const updatePassword = async (newPassword) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;

    return {
      success: true,
      message: 'Password updated successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId, role, profileData) => {
  try {
    let table;
    switch (role) {
      case 'admin':
        table = 'admins';
        break;
      case 'vendor':
        table = 'vendors';
        break;
      case 'customer':
        table = 'customers';
        break;
      default:
        throw new Error('Invalid role');
    }

    const { data, error } = await supabase
      .from(table)
      .update(profileData)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      profile: data
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(async (event, session) => {
    if (session?.user) {
      const role = await getUserRole(session.user.id);
      const profile = await getUserProfile(session.user.id, role);
      callback(event, session, role, profile);
    } else {
      callback(event, null, null, null);
    }
  });
};

export default {
  signUp,
  signIn,
  signInWithProvider,
  signOut,
  getCurrentUserWithRole,
  resetPassword,
  updatePassword,
  updateUserProfile,
  onAuthStateChange
};

